import React, { FCWithChildren, useState } from 'react';
import { createContext, useContext } from 'use-context-selector';
import {
  HotFilterItem,
  HotFiltersPaginationState,
  HotFiltersPaginationStatsResponse,
} from '@interfaces/api/responses/hot-filters.response';
import { FavoriteProduct } from '@interfaces/models/favoriteProduct';
import { useRouter } from 'next/router';
import { FAV_CONSTANTS } from '@constants/favorites';
import useUser from '@hooks/user/use-user';

export interface HotFiltersContextProps {
  filteredItems: FavoriteProduct[];
  hotFilters: HotFilterItem[];
  setHotFilters: (children: HotFilterItem[]) => void;
  setFilteredItems: (children: FavoriteProduct[]) => void;
  appliedHotFilters: Record<string, string> | {};
  setAppliedHotFilters: (filters: Record<string, string> | {}) => void;
  totalCount: number;
  setTotalCount: (arg: number) => void;
  pagination: HotFiltersPaginationState; // paginationMeta
  paginationQuery: Partial<HotFiltersPaginationStatsResponse>;
  setPaginationQuery: (arg) => void;
  setPagination: (arg: HotFiltersPaginationState) => void;
  openedHotFilter: HotFilterItem | null;
  setOpenedHotFilter: (filter: HotFilterItem | null) => void;
  isUserSlug: boolean;
}

export type HotFiltersProviderProps = {};

const HotFiltersContext = createContext<HotFiltersContextProps>({} as never);

const HotFiltersProvider: FCWithChildren<HotFiltersProviderProps> = (props): React.JSX.Element => {
  const { children } = props;
  const router = useRouter();
  const { user } = useUser();
  const { sortBy, favoritesUserSlug, profileSlug, page, pageSize, ...filters } = router?.query as {
    sortBy?: string;
    favoritesUserSlug?: string;
    profileSlug?: string;
    page?: string;
    pageSize?: string;
  };

  const slug = favoritesUserSlug?.[0] || profileSlug?.[0];
  const isUserSlug = slug === user?.id;
  const initialPage = parseInt(page || '1', 10);
  // Remove page specific default limit (FAV_CONSTANTS)
  const initialLimit = parseInt(pageSize) || FAV_CONSTANTS.FAV_DEFAULT_LIMIT;
  const initialOffset = (initialPage - 1) * initialLimit;

  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [pagination, setPagination] = useState<HotFiltersPaginationState>({
    currentPage: initialPage,
    totalPages: 0,
    totalCount: 0,
  });
  const [paginationQuery, setPaginationQuery] = useState<Partial<HotFiltersPaginationStatsResponse>>({
    limit: initialLimit,
    offset: initialOffset,
  });
  const [hotFilters, setHotFilters] = useState<HotFilterItem[]>([]);
  const [filteredItems, setFilteredItems] = useState<FavoriteProduct[] | null>(null);
  const [appliedHotFilters, setAppliedHotFilters] = useState<Record<string, string> | {}>(filters || {});
  const [openedHotFilter, setOpenedHotFilter] = useState<HotFilterItem | null>(null);

  const value: HotFiltersContextProps = {
    hotFilters,
    setHotFilters,
    setFilteredItems,
    filteredItems,
    setAppliedHotFilters,
    appliedHotFilters,
    totalCount,
    pagination,
    paginationQuery,
    setPaginationQuery,
    setPagination,
    setTotalCount,
    openedHotFilter,
    setOpenedHotFilter,
    isUserSlug,
  };

  return <HotFiltersContext.Provider value={value}>{children}</HotFiltersContext.Provider>;
};

const useHotFiltersContext = () => useContext(HotFiltersContext);

export { HotFiltersProvider, useHotFiltersContext };
